<script setup lang="ts">
import type { AccountIconName } from './account-icons.ts'

/**
 * A user-chosen glyph for an account, drawn on the same 16-grid and 1.7-unit stroke as
 * KindIcon rather than imported from an icon library - the app has never shipped a second
 * icon mechanism, and this stays the same hand. Unlike KindIcon's four fixed states, the
 * value here is free text on the wire (contract `icon` field), so an unrecognised value
 * (a stale fixture, a future addition) falls back to a quiet ring instead of rendering
 * nothing.
 */
defineProps<{
  icon: string
  label: string
}>()
</script>

<template>
  <svg
    width="15"
    height="15"
    viewBox="0 0 16 16"
    role="img"
    :aria-label="label"
    class="block shrink-0"
    fill="none"
    stroke="currentColor"
    stroke-width="1.7"
    stroke-linecap="butt"
    stroke-linejoin="miter"
  >
    <g v-if="(icon as AccountIconName) === 'BANK'">
      <path d="M2 6 8 2 14 6" />
      <path d="M2 6h12" />
      <path d="M4 6v6M8 6v6M12 6v6" />
      <path d="M2 12h12" />
    </g>
    <g v-else-if="(icon as AccountIconName) === 'CASH'">
      <rect x="2" y="4" width="12" height="8" rx="1" />
      <circle cx="8" cy="8" r="1.8" />
    </g>
    <g v-else-if="(icon as AccountIconName) === 'CARD'">
      <rect x="2" y="4" width="12" height="8" rx="1.4" />
      <path d="M2 7h12" />
    </g>
    <g v-else-if="(icon as AccountIconName) === 'WALLET'">
      <path d="M2 5 4 2h8l2 3" />
      <rect x="2" y="5" width="12" height="7" rx="1" />
      <path d="M10.2 8.5h2" />
    </g>
    <g v-else-if="(icon as AccountIconName) === 'CHART'">
      <path d="M2 14h12" />
      <path d="M4 14v-4M8 14v-7M12 14v-2.5" />
    </g>
    <g v-else-if="(icon as AccountIconName) === 'INVESTMENT'">
      <path d="M2 12 6.3 7.7 9 10.2 13.5 3.5" />
      <path d="M10.3 3.5h3.2v3.2" />
    </g>
    <g v-else-if="(icon as AccountIconName) === 'CRYPTO'">
      <path d="M8 2 13 5v6L8 14 3 11V5Z" />
      <path d="M8 6 10 8 8 10 6 8Z" />
    </g>
    <g v-else-if="(icon as AccountIconName) === 'SAVINGS'">
      <ellipse cx="8" cy="11" rx="5" ry="2" />
      <path d="M3 11V7M13 11V7" />
      <ellipse cx="8" cy="7" rx="5" ry="2" />
    </g>
    <g v-else-if="(icon as AccountIconName) === 'HOME'">
      <path d="M2 8 8 2 14 8" />
      <path d="M4 8v6h8V8" />
    </g>
    <g v-else-if="(icon as AccountIconName) === 'SHOPPING'">
      <path d="M3 6h10l-1 8H4Z" />
      <path d="M5.5 6a2.5 2.5 0 0 1 5 0" />
    </g>
    <g v-else-if="(icon as AccountIconName) === 'GIFT'">
      <rect x="2" y="7" width="12" height="7" />
      <path d="M1.5 5h13v2h-13Z" />
      <path d="M8 5v9" />
      <path d="M8 5 5.5 2.5 4.5 3.5ZM8 5l2.5-2.5 1 1Z" />
    </g>
    <g v-else-if="(icon as AccountIconName) === 'GLOBAL'">
      <circle cx="8" cy="8" r="6" />
      <path d="M2 8h12" />
      <ellipse cx="8" cy="8" rx="2.3" ry="6" />
    </g>
    <circle v-else cx="8" cy="8" r="4" />
  </svg>
</template>
